import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
// RadioGroup still used for channel selection
import { Loader2, Send, Users, User, Shield, Search } from 'lucide-react';
import { toast } from 'sonner';
import { adminNotificationAPI } from '../../services/api';

const ROLE_OPTIONS = [
  { value: 'student', label: 'Students' },
  { value: 'agent', label: 'Agents' },
  { value: 'counselor', label: 'Counselors' },
  { value: 'super_admin', label: 'Super Admins' },
];

const TYPE_OPTIONS = [
  { value: 'GENERAL', label: 'General' },
  { value: 'SYSTEM', label: 'System' },
];

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low', color: 'text-gray-600' },
  { value: 'NORMAL', label: 'Normal', color: 'text-blue-600' },
  { value: 'HIGH', label: 'High', color: 'text-amber-600' },
  { value: 'URGENT', label: 'Urgent', color: 'text-red-600' },
];

const CHANNEL_OPTIONS = [
  { value: 'DASHBOARD', label: 'Dashboard Only' },
  { value: 'EMAIL', label: 'Email Only' },
  { value: 'BOTH', label: 'Both' },
];

export default function CreateNotificationDialog({ open, onOpenChange, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipients, setRecipients] = useState({ users: [] });
  const [userSearch, setUserSearch] = useState('');
  const [formData, setFormData] = useState({
    targetType: 'ALL',
    targetRole: '',
    targetUserId: '',
    type: 'GENERAL',
    priority: 'NORMAL',
    channel: 'BOTH',
    title: '',
    message: '',
    actionUrl: '',
    actionText: '',
  });
  const [errors, setErrors] = useState({});

  // Fetch recipients when targetType is USER
  useEffect(() => {
    if (formData.targetType === 'USER' && open) {
      fetchRecipients();
    }
  }, [formData.targetType, open]);

  const fetchRecipients = async () => {
    setLoadingRecipients(true);
    try {
      // Fetch all users with a high limit for client-side filtering
      const response = await adminNotificationAPI.getRecipients({ limit: 500 });
      // Handle response structure - API returns data directly
      const data = response.data || response;
      setRecipients(data || { users: [] });
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
      toast.error('Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  // Helper to get user full name
  const getUserName = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.firstName || user.lastName || user.email || 'Unknown';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be 200 characters or less';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length > 2000) {
      newErrors.message = 'Message must be 2000 characters or less';
    }

    if (formData.targetType === 'ROLE' && !formData.targetRole) {
      newErrors.targetRole = 'Please select a role';
    }

    if (formData.targetType === 'USER' && !formData.targetUserId) {
      newErrors.targetUserId = 'Please select a user';
    }

    if (formData.actionUrl && !isValidUrl(formData.actionUrl)) {
      newErrors.actionUrl = 'Please enter a valid URL';
    }

    if (formData.actionText && formData.actionText.length > 50) {
      newErrors.actionText = 'Action text must be 50 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      // Allow relative URLs
      if (string.startsWith('/')) return true;
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        targetType: formData.targetType,
        title: formData.title.trim(),
        message: formData.message.trim(),
        type: formData.type,
        priority: formData.priority,
        channel: formData.channel,
      };

      if (formData.targetType === 'ROLE') {
        payload.targetRole = formData.targetRole;
      }

      if (formData.targetType === 'USER') {
        payload.targetUserId = formData.targetUserId;
      }

      if (formData.actionUrl) {
        payload.actionUrl = formData.actionUrl.trim();
      }

      if (formData.actionText) {
        payload.actionText = formData.actionText.trim();
      }

      const response = await adminNotificationAPI.create(payload);

      const stats = response.data?.stats || {};
      toast.success(`Notification sent to ${stats.total || 0} recipient(s)`);

      // Reset form
      setFormData({
        targetType: 'ALL',
        targetRole: '',
        targetUserId: '',
        type: 'GENERAL',
        priority: 'NORMAL',
        channel: 'BOTH',
        title: '',
        message: '',
        actionUrl: '',
        actionText: '',
      });
      setErrors({});

      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send notification';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleTargetTypeChange = (targetType) => {
    setFormData(prev => ({
      ...prev,
      targetType,
      targetRole: '',
      targetUserId: '',
    }));
    setErrors({});
  };

  const getTargetDescription = () => {
    switch (formData.targetType) {
      case 'ALL':
        return 'This notification will be sent to all users in the system.';
      case 'ROLE':
        return formData.targetRole
          ? `This notification will be sent to all users with the "${formData.targetRole}" role.`
          : 'Select a role to target specific users.';
      case 'USER':
        return 'This notification will be sent to a specific user.';
      default:
        return '';
    }
  };

  const filteredUsers = recipients.users?.filter(user => {
    if (!userSearch) return true;
    const searchLower = userSearch.toLowerCase();
    const fullName = getUserName(user).toLowerCase();
    return (
      fullName.includes(searchLower) ||
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  }) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Send Notification
          </DialogTitle>
          <DialogDescription>
            Create and send a notification to users. Choose who should receive it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-4">
          {/* Target Type Selection */}
          <div className="space-y-3">
            <Label>Send To</Label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => handleTargetTypeChange('ALL')}
                className={`flex flex-col items-center justify-between rounded-md border-2 p-4 hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors ${
                  formData.targetType === 'ALL'
                    ? 'border-[#F2575F] bg-primary/5'
                    : 'border-muted bg-transparent'
                }`}
              >
                <Users className="mb-2 h-5 w-5" />
                <span className="text-sm font-medium">All Users</span>
              </button>
              <button
                type="button"
                onClick={() => handleTargetTypeChange('ROLE')}
                className={`flex flex-col items-center justify-between rounded-md border-2 p-4 hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors ${
                  formData.targetType === 'ROLE'
                    ? 'border-[#F2575F] bg-primary/5'
                    : 'border-muted bg-transparent'
                }`}
              >
                <Shield className="mb-2 h-5 w-5" />
                <span className="text-sm font-medium">By Role</span>
              </button>
              <button
                type="button"
                onClick={() => handleTargetTypeChange('USER')}
                className={`flex flex-col items-center justify-between rounded-md border-2 p-4 hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors ${
                  formData.targetType === 'USER'
                    ? 'border-[#F2575F] bg-primary/5'
                    : 'border-muted bg-transparent'
                }`}
              >
                <User className="mb-2 h-5 w-5" />
                <span className="text-sm font-medium">Specific User</span>
              </button>
            </div>
            <p className="text-sm text-muted-foreground">{getTargetDescription()}</p>
          </div>

          {/* Role Selection (when ROLE is selected) */}
          {formData.targetType === 'ROLE' && (
            <div className="space-y-2">
              <Label htmlFor="targetRole">
                Select Role <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.targetRole}
                onValueChange={(value) => handleChange('targetRole', value)}
              >
                <SelectTrigger className={errors.targetRole ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Choose a role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.targetRole && (
                <p className="text-sm text-red-500">{errors.targetRole}</p>
              )}
            </div>
          )}

          {/* User Selection (when USER is selected) */}
          {formData.targetType === 'USER' && (
            <div className="space-y-2">
              <Label htmlFor="targetUserId">
                Select User <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9 mb-2"
                />
              </div>
              <Select
                value={formData.targetUserId}
                onValueChange={(value) => handleChange('targetUserId', value)}
              >
                <SelectTrigger className={errors.targetUserId ? 'border-red-500' : ''}>
                  <SelectValue placeholder={loadingRecipients ? "Loading..." : "Choose a user"} />
                </SelectTrigger>
                <SelectContent>
                  {loadingRecipients ? (
                    <div className="p-2 text-center text-sm text-muted-foreground">
                      Loading users...
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-2 text-center text-sm text-muted-foreground">
                      No users found
                    </div>
                  ) : (
                    filteredUsers.map((user) => (
                      <SelectItem key={user.userId} value={user.userId}>
                        {getUserName(user)} ({user.email}) - {user.role}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {errors.targetUserId && (
                <p className="text-sm text-red-500">{errors.targetUserId}</p>
              )}
            </div>
          )}

          {/* Type and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => handleChange('type', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => handleChange('priority', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((priority) => (
                    <SelectItem key={priority.value} value={priority.value}>
                      <span className={priority.color}>{priority.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Channel */}
          <div className="space-y-2">
            <Label>Delivery Channel</Label>
            <RadioGroup
              value={formData.channel}
              onValueChange={(value) => handleChange('channel', value)}
              className="flex gap-4"
            >
              {CHANNEL_OPTIONS.map((channel) => (
                <div key={channel.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={channel.value} id={`channel-${channel.value}`} />
                  <Label htmlFor={`channel-${channel.value}`} className="cursor-pointer">
                    {channel.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter notification title"
              className={errors.title ? 'border-red-500' : ''}
              maxLength={200}
            />
            <div className="flex justify-between">
              {errors.title ? (
                <p className="text-sm text-red-500">{errors.title}</p>
              ) : (
                <span />
              )}
              <p className="text-xs text-muted-foreground">{formData.title.length}/200</p>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">
              Message <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Enter notification message"
              className={`min-h-[100px] ${errors.message ? 'border-red-500' : ''}`}
              maxLength={2000}
            />
            <div className="flex justify-between">
              {errors.message ? (
                <p className="text-sm text-red-500">{errors.message}</p>
              ) : (
                <span />
              )}
              <p className="text-xs text-muted-foreground">{formData.message.length}/2000</p>
            </div>
          </div>

          {/* Action URL and Text (Optional) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="actionUrl">Action URL (Optional)</Label>
              <Input
                id="actionUrl"
                value={formData.actionUrl}
                onChange={(e) => handleChange('actionUrl', e.target.value)}
                placeholder="/dashboard or https://..."
                className={errors.actionUrl ? 'border-red-500' : ''}
              />
              {errors.actionUrl && (
                <p className="text-sm text-red-500">{errors.actionUrl}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="actionText">Action Text (Optional)</Label>
              <Input
                id="actionText"
                value={formData.actionText}
                onChange={(e) => handleChange('actionText', e.target.value)}
                placeholder="View Details"
                className={errors.actionText ? 'border-red-500' : ''}
                maxLength={50}
              />
              {errors.actionText && (
                <p className="text-sm text-red-500">{errors.actionText}</p>
              )}
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send Notification
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
